import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import BookingDialog from "@/components/BookingDialog";
import { X } from "lucide-react";

const galleryImages = [
  {
    url: "/images/barberia.jpg",
    category: "interior",
    title: "Il Salone",
  },
  {
    url: "/images/gallery-logo.jpg",
    category: "interior",
    title: "Logo & Decori",
  },
  {
    url: "/images/interior-2.jpg",
    category: "interior",
    title: "Atmosfera",
  },
  {
    url: "/images/poltrone.jpg",
    category: "interior",
    title: "Le Poltrone",
  },
  {
    url: "/images/logo-parete.jpg",
    category: "interior",
    title: "Il Brand",
  },
  {
    url: "/images/lavaggio.jpg",
    category: "interior",
    title: "Zona Lavaggio",
  },
  {
    url: "/images/utensili.jpg",
    category: "work",
    title: "Utensili Premium",
  },
];

const categories = [
  { id: "all", label: "Tutti" },
  { id: "interior", label: "Il Salone" },
  { id: "work", label: "Il Lavoro" },
];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" },
};

export const GalleriaPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages = activeCategory === "all" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  return (
    <div className="min-h-screen bg-kairos-black" data-testid="galleria-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6" data-testid="galleria-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p
            className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Galleria
          </motion.p>
          <motion.h1
            className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-6 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Il mio mondo
          </motion.h1>
          <motion.p
            className="text-white/60 max-w-xl mx-auto text-base md:text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Esplora l'atmosfera unica di Kairos La Barberia
          </motion.p>
        </div>
      </section>

      {/* Filter */}
      <section className="pb-8 px-6" data-testid="gallery-filter">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center gap-4 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-2 text-sm uppercase tracking-wider transition-all duration-300 ${
                  activeCategory === cat.id
                    ? "bg-kairos-gold text-kairos-black"
                    : "border border-white/20 text-white/60 hover:border-kairos-gold hover:text-kairos-gold"
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-8 px-6" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            <AnimatePresence mode="popLayout">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.url}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="relative aspect-[4/3] overflow-hidden cursor-pointer group"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${index}`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-kairos-black/0 group-hover:bg-kairos-black/50 transition-all duration-500" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <p className="text-white font-serif text-xl">{image.title}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-kairos-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
              data-testid="lightbox-close"
            >
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage.url}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white font-serif text-xl">
              {selectedImage.title}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-24 px-6 bg-kairos-gray/20" data-testid="galleria-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6 tracking-tight">
              Vivi l'esperienza dal vivo
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-10">
              Le foto non bastano. Vieni a scoprire di persona l'atmosfera unica di Kairos.
            </p>
            <button
              onClick={() => setBookingOpen(true)}
              className="bg-kairos-gold text-kairos-black px-12 py-5 text-sm uppercase tracking-[0.2em] font-bold hover:bg-white transition-all duration-300"
              data-testid="galleria-book-btn"
            >
              Prenota Ora
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>
  );
};

export default GalleriaPage;

